import { apiFetch, type TokenStore } from './client.ts';
import type { EngagedByParty } from '../lib/roles.ts';

/**
 * Client/parent portal API (read-only). GET /portal/overview returns the
 * logged-in parent's own child's programme - active skill + behaviour targets -
 * and the documents the practice has shared for the case. Scoped server-side to
 * the actor's active client_contact case memberships.
 */

export interface PortalSubTarget {
  id: string;
  label: string;
  status: string;
}

export interface PortalSkillTarget {
  id: string;
  name: string;
  area: string;
  status: string;
  /** ABA data type - drives the home-session recording control (9-A). */
  dataType: string;
  /** Sub-targets (non-discontinued) - each chartable in the portal. */
  subTargets: PortalSubTarget[];
}

export interface PortalBehaviourTarget {
  id: string;
  name: string;
  status: string;
}

/** A session as the client sees it — the clinician's note included (SPEC §3, 2026-08-21). */
export interface PortalSession {
  id: string;
  startedAt: number;
  endedAt: number | null;
  encounterType: string;
  status: string;
  conductedByName: string;
  notes: string | null;
}

export interface PortalDocument {
  documentId: string;
  kindCode: string;
  title: string;
  fileObjectId: string | null;
}

export interface PortalInvoiceLine {
  description: string;
  lineDate: number | null;
  quantityHundredths: number;
  rateMinor: number;
  totalMinor: number;
}
/** An invoice this reader can review (V6 gap 12-A) - one addressed to them. */
export interface PortalInvoice {
  id: string;
  invoiceNumber: string;
  totalMinor: number;
  currency: string;
  status: string;
  periodStart: number;
  periodEnd: number;
  payerResponse: 'accepted' | 'rejected' | null;
  payerResponseReason: string | null;
  /** true when the client may accept/reject (issued|sent and not yet responded). */
  canRespond: boolean;
  /** B5 - full detail so accept/reject is an informed decision. */
  lineItems: PortalInvoiceLine[];
  dueAt: number | null;
  paymentInstructions: string | null;
  pdfFileObjectId: string | null;
  // Human finding 2026-07-06 #2b - who the invoice is from, when it was issued,
  // and how to pay. From the issue-time issuer snapshot (frozen, invariant 9).
  issuedAt: number | null;
  issuer: { name: string; address: string | null } | null;
  bank: {
    accountName: string | null;
    sortCode: string | null;
    accountNumber: string | null;
    iban: string | null;
  } | null;
}

/** A support worker (tutor) on the case, with the client's confirmation status. */
export interface PortalSupportWorker {
  userId: string;
  name: string;
  /** confirmed = active tutor_access consent; pending = practice requires it,
   *  none yet; blocked = no live grant and none being waited for, so the server refuses
   *  this tutor everything on the case. */
  status: 'confirmed' | 'pending' | 'blocked';
  /** id of the active tutor_access consent, for withdrawal (null when not confirmed). */
  consentId: string | null;
  /** 'client' = the client engaged this tutor; 'practice' = the clinic placed them.
   *  Was two fields carrying one answer until the domain lost its unwritten third
   *  value ('third_party') and they became identical. */
  engagedByParty: EngagedByParty;
}

/** A tutor the client has nominated for the case (client-initiated, increment 3). */
export interface PortalNomination {
  id: string;
  nominatedEmail: string;
  nominatedName: string | null;
  status: 'pending' | 'authorised' | 'declined';
  decisionReason: string | null;
}

/** The service agreement + cancellation state for the case (item B). */
export interface PortalAgreement {
  contractId: string;
  status: string;
  /** The signed agreement PDF (file_objects id), downloadable via GET /files/:id.
   *  Null while the PDF is still being generated. */
  signedFileObjectId: string | null;
  signingMethod: 'electronic' | 'offline_attested' | null;
  cancellationByParty: 'practice' | 'client' | null;
  cancellationNoticeEndAt: number | null;
  cancellationReason: string | null;
}

/** A behaviour support plan (BSP) proposed by the practice, awaiting the client's
 *  agreement. Consenting in the portal unblocks the practitioner activating it. */
export interface PortalBehaviourSupportPlan {
  id: string;
  behaviourTargetName: string | null;
  status: string;
  planNotes: string | null;
  fba: {
    assessmentType: string;
    hypothesis: string;
    evidenceSummary: string | null;
    conductedOn: number;
  } | null;
  /** Whether THIS plan (not the case as a whole) has a granted consent. */
  consentGranted: boolean;
}

export interface PortalRegistration { body: string; provenance: 'verified' | 'self_declared' }
export interface PortalCareTeamMember {
  userId: string;
  name: string;
  isLead: boolean;
  /** All of this clinician's registrations (a practitioner may hold several). */
  registrations: PortalRegistration[];
}
export interface PortalCase {
  caseId: string;
  /** The service recipient's name - may be a child, young adult, or adult.
   *  Use isSelf/caseType for copy; never assume "child". */
  recipientName: string;
  /** The name of the managing practice. */
  practiceName: string;
  /** Display name of the primary practitioner (case lead), null when vacant. */
  practitionerName: string | null;
  /** The lead's professional registrations with honest provenance (verified by practice
   *  vs self-declared). A practitioner may hold several (e.g. BCBA + UK-SBA) - all shown.
   *  Empty when none on record. Never a bare "verified". */
  practitionerRegistrations: PortalRegistration[];
  /** All clinicians on the case (lead first), each with registration (T1). */
  careTeam: PortalCareTeamMember[];
  /** child | young_adult | adult. */
  caseType: string;
  /** true when the viewer IS the service recipient (self-representing). */
  isSelf: boolean;
  caseStatus: string;
  /** Item 4.5: true when the case lost its lead practitioner and the practice is
   *  reassigning - shown as neutral "assigning a new lead" copy. */
  reassignmentPending: boolean;
  skillTargets: PortalSkillTarget[];
  behaviourTargets: PortalBehaviourTarget[];
  documents: PortalDocument[];
  sessions: PortalSession[];
  /** Invoices addressed to THIS reader, reviewable here. */
  invoices: PortalInvoice[];
  requiresSupportWorkerConfirmation: boolean;
  supportWorkers: PortalSupportWorker[];
  nominations: PortalNomination[];
  /** The service agreement + cancellation state, when present (item B). */
  agreement: PortalAgreement | null;
  /** BSPs proposed by the practice awaiting the client's agreement. Each carries its own
   *  consentGranted — a case may hold several concurrent BSPs. */
  behaviourSupportPlans: PortalBehaviourSupportPlan[];
  /** True when the practice has confirmed this client has received data-entry training. */
  dataEntryPermitted: boolean;
  /**
   * ADR 0006 - may this client move a sub-target's status along? Decided
   * server-side with the same can() the write path uses, so the UI renders the
   * answer instead of re-deriving the rule (v7.79 capability-flag pattern).
   */
  canProgressSubTargets: boolean;
  /** Why not (denial code) when canProgressSubTargets is false; null when allowed. */
  subTargetProgressionDenialCode: string | null;
}

export async function getPortalOverview(store: TokenStore): Promise<PortalCase[]> {
  const res = await apiFetch<{ cases: PortalCase[] }>('/portal/overview', {}, store);
  return res.cases;
}

